import { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  Cog6ToothIcon,
  UserPlusIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../config/rolePermissions';
import AddAccountModal from '../auth/AddAccountModal';

function initialsOf(name = '') {
  return name.split(' ').filter(Boolean).map((s) => s[0]).slice(0, 2).join('').toUpperCase() || '?';
}

function Avatar({ name, size = 'md' }) {
  const dim = size === 'sm' ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs';
  return (
    <div className={clsx(
      'rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 text-white flex items-center justify-center font-bold shadow-md shadow-brand-500/20 shrink-0',
      dim,
    )}>
      {initialsOf(name)}
    </div>
  );
}

/**
 * Top-nav account menu with:
 *   - Current user header (initials avatar, name, email)
 *   - Switcher row per saved account (checkmark = active)
 *   - "Add another account" → opens a modal (does NOT navigate — the current
 *     session stays live while the user types the second account's creds)
 *   - Settings
 *   - "Log out" — sign out this account only (silent switch if another exists)
 *   - "Log out of all devices" — global revoke
 */
export default function NavUserMenu() {
  const { user, accounts, activeAccountId, switchAccount, logout, logoutAll, removeAccount } = useAuth();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  const onSwitch = async (id) => {
    if (id === activeAccountId) return;
    try {
      await switchAccount(id);
      toast.success('Switched account');
    } catch (err) {
      toast.error(err.message || 'Could not switch account');
      navigate('/login');
    }
  };

  const onLogout = async () => {
    try { await logout(); }
    catch (err) { toast.error(err.message || 'Logout failed'); }
    navigate('/login');
  };

  const onLogoutAll = async () => {
    try {
      await logoutAll();
      toast.success('Signed out of all devices');
    } catch (err) {
      toast.error(err.message || 'Logout failed');
    }
    navigate('/login');
  };

  const onRemove = async (e, id) => {
    e.stopPropagation();
    try { await removeAccount(id); }
    catch (err) { toast.error(err.message || 'Could not remove account'); }
  };

  const otherAccounts = accounts.filter((a) => a.id !== activeAccountId);

  return (
    <>
      <Menu as="div" className="relative shrink-0">
        <Menu.Button
          className="flex items-center gap-2 rounded-xl p-1 hover:bg-surface-100 transition-colors ring-1 ring-transparent hover:ring-surface-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
          aria-label="Account menu"
        >
          <Avatar name={user?.name} />
          <span className="hidden md:block text-left min-w-0 max-w-[140px]">
            <span className="block text-sm font-semibold text-dark-900 truncate">{user?.name}</span>
            <span className="block text-2xs text-surface-500 truncate">
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </span>
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 top-full mt-2 w-72 origin-top-right rounded-2xl bg-white shadow-card-hover border border-surface-100 p-1.5 focus:outline-none z-50">
            {/* Current user */}
            <div className="px-3 py-2.5 border-b border-surface-100 mb-1 flex items-center gap-3">
              <Avatar name={user?.name} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-dark-900 truncate">{user?.name}</p>
                <p className="text-2xs text-surface-500 truncate">{user?.email}</p>
              </div>
              <CheckCircleIcon className="h-4 w-4 text-success-500 shrink-0 ml-auto" title="Active" />
            </div>

            {/* Other saved accounts */}
            {otherAccounts.length > 0 && (
              <>
                <p className="px-3 pt-1 pb-1 text-[10px] font-semibold uppercase tracking-widest text-surface-400">Switch account</p>
                {otherAccounts.map((a) => (
                  <Menu.Item key={a.id}>
                    {({ active }) => (
                      <div
                        className={clsx(
                          'relative group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl cursor-pointer',
                          active && 'bg-surface-50',
                        )}
                        onClick={() => onSwitch(a.id)}
                      >
                        <Avatar name={a.name} size="sm" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-dark-900 truncate">{a.name}</p>
                          <p className="text-2xs text-surface-500 truncate">{a.email}</p>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => onRemove(e, a.id)}
                          className="h-6 w-6 rounded-full text-surface-400 hover:text-error-600 hover:bg-error-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center shrink-0"
                          title="Remove this account"
                        >
                          <XMarkIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </Menu.Item>
                ))}
                <div className="border-t border-surface-100 my-1" />
              </>
            )}

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => setAddOpen(true)}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-surface-700',
                    active && 'bg-surface-50',
                  )}
                >
                  <UserPlusIcon className="h-4 w-4 shrink-0" />
                  Add another account
                </button>
              )}
            </Menu.Item>

            <div className="border-t border-surface-100 my-1" />

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => navigate('/settings')}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-surface-700',
                    active && 'bg-surface-50',
                  )}
                >
                  <Cog6ToothIcon className="h-4 w-4 shrink-0" />
                  Settings
                </button>
              )}
            </Menu.Item>

            <div className="border-t border-surface-100 my-1" />

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onLogout}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-error-600',
                    active && 'bg-error-50',
                  )}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4 shrink-0" />
                  Log out{otherAccounts.length > 0 ? ' current account' : ''}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={onLogoutAll}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-error-700',
                    active && 'bg-error-50',
                  )}
                >
                  <ArrowLeftOnRectangleIcon className="h-4 w-4 shrink-0" />
                  Log out of all devices
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>

      <AddAccountModal open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}
